// components/OrderPricingSummary.jsx
import { useState } from "react";
import "./OrderPricingSummary.css";
import {
  ClipboardList,
  ChevronUp,
  ChevronDown,
  Info,
  Tag,
  ReceiptText,
  Wrench,
  Truck,
  User,
  Store,
  Check,
  AlertCircle,
  X,
} from "lucide-react";

const OrderPricingSummary = ({ pricing }) => {
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  if (!pricing) return null;

  const hasDiscounts = pricing.discount_total > 0;
  const discountStatus = pricing.discount_summary?.status;

const badgeConfig = {
  all_applied: {
    label: pricing.discount_summary?.label || "All discounts applied",
    className: "ops-badge ops-badge--success",
    icon: <Check size={13} />,
  },
  some_not_applied: {
    label: pricing.discount_summary?.label || "Some discounts were not applied",
    className: "ops-badge ops-badge--warning",
    icon: <AlertCircle size={13} />,
  },
  none_applied: {
    label: pricing.discount_summary?.label || "No discounts applied",
    className: "ops-badge ops-badge--warning",
    icon: <AlertCircle size={13} />,
  },
}[discountStatus] || {
  label: "All discounts applied",
  className: "ops-badge ops-badge--success",
  icon: <Check size={13} />,
};
  return (
    <>
      <div className="ops-card mb-3">
        {/* Header */}
        <div
          className="ops-header"
          onClick={() => setIsOpen((prev) => !prev)}
          style={{ cursor: "pointer" }}
        >
          <span className="ops-title">
            <ClipboardList size={18} />
            Order Summary
          </span>
          {isOpen ? (
            <ChevronUp size={18} className="ops-chevron" />
          ) : (
            <ChevronDown size={18} className="ops-chevron" />
          )}
        </div>
        {isOpen && (
          <div className="p-2 mt-2 border-top border-1">
            {/* Subtotal */}
            <div className="ops-row">
              <span className="ops-label">
                Sub Total (
                {pricing.totals?.subtotal?.label?.match(/\d+/)?.[0] || 1}{" "}
                services ) :
              </span>
              <span className="ops-value">
                {pricing.formatted?.subtotal} EGP
              </span>
            </div>
            {/* Discounts */}
            {hasDiscounts && (
              <>
                <div className="ops-row ops-row--align-start">
                  <div className="ops-discount-left">
                    <span className="ops-label">Discounts:</span>
                    <span className={badgeConfig.className}>
                      {badgeConfig.icon}
                      {badgeConfig.label}
                    </span>
                  </div>
                  <button
                    className="ops-info-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDiscountModalOpen(true);
                    }}
                    aria-label="Discount details"
                  >
                    <Info size={16} />
                  </button>
                </div>
                <div className="ops-discount-chips">
                  {pricing.discounts?.map((discount, idx) => (
                    <div className="ops-chip" key={idx}>
                      <i className="ti ti-tag" aria-hidden="true" />
                      <span className="ops-chip-label">{discount.label}:</span>
                      <span className="ops-chip-val">
                        -{discount.formatted_amount} EGP
                      </span>
                    </div>
                  ))}
                  <span className="ops-discount-total-val ms-auto">
                    -{pricing.formatted?.discount_total} EGP
                  </span>
                </div>
                {pricing.discount_summary?.covered_by && (
                  <div className="ops-covered">
                    Discount covered by:{" "}
                    {pricing.discount_summary.covered_by.estbn > 0 &&
                      `ESTBN ${pricing.discount_summary.covered_by.formatted.estbn} EGP  `}
                    {pricing.discount_summary.covered_by.provider > 0 &&
                      `Provider ${pricing.discount_summary.covered_by.formatted.provider} EGP  `}
                    {pricing.discount_summary.covered_by.unknown > 0 &&
                      `Unknown ${pricing.discount_summary.covered_by.formatted.unknown} EGP`}
                  </div>
                )}
              </>
            )}
            {/* Taxes */}
            {pricing.taxes?.items?.length > 0 && (
              <>
                <div className="ops-section-label">Taxes:</div>
                <div className="ops-inline-items d-flex justify-content-between align-items-center">
                  {pricing.taxes.items.map((tax, idx) => (
                    <div className="ops-inline-chip" key={idx}>
                      <i className="ti ti-receipt-tax" aria-hidden="true" />
                      <span className="ops-chip-label">{tax.label}:</span>
                      <span className="ops-value">{tax.formatted} EGP</span>
                    </div>
                  ))}
                  <span className="ops-section-total">
                    {pricing.taxes.formatted_total} EGP
                  </span>
                </div>
                <div className="ops-note">{pricing.taxes.note}</div>
              </>
            )}
            {/* Fees */}
            <div className="ops-section-label">Fees Summary:</div>
            <div className="ops-inline-items">
              {pricing.fees?.items?.length > 0 ? (
                <>
                  {pricing.fees.items.map((fee, idx) => (
                    <div className="ops-inline-chip" key={idx}>
                      <i className="ti ti-tool" aria-hidden="true" />
                      <span className="ops-chip-label">{fee.label}:</span>
                      <span className="ops-value">{fee.formatted} EGP</span>
                    </div>
                  ))}
                  <span className="ops-section-total">
                    {pricing.fees.formatted_total} EGP
                  </span>
                </>
              ) : (
                <div className="ops-inline-chip">
                  <i className="ti ti-truck-delivery" aria-hidden="true" />
                  <span className="ops-chip-label">Delivery Fee:</span>
                  <span className="ops-value">
                    {pricing.formatted?.delivery_fee} EGP
                  </span>
                </div>
              )}
            </div>
            <div className="ops-note">{pricing.fees?.note}</div>
            <hr className="ops-divider" />
            {/* Total */}
            <div className="ops-total-row">
              <span className="ops-total-label">Total:</span>
              <span className="ops-total-value">
                {pricing.formatted?.grand_total} EGP
              </span>
            </div>
            {/* Revenue */}
            <div className="ops-section-label" style={{ marginTop: "10px" }}>
              Revenue Breakdown:
            </div>
            <div className="ops-revenue-row">
              {pricing.revenue?.items
                // ?.filter((r) =>
                //   ["service_provider_share", "estbn_net_earnings"].includes(
                //     r.key,
                //   ),
                // )
                ?.map((item, idx) => (
                  <div className="ops-revenue-chip" key={idx}>
                    <i
                      className={`ti ${
                        item.key === "service_provider_share"
                          ? "ti-user"
                          : "ti-building-store"
                      }`}
                      aria-hidden="true"
                    />
                    <span className="ops-revenue-chip-label">
                      {item.label}:
                    </span>
                    <span className="ops-revenue-chip-val">
                      {item.formatted} EGP
                    </span>
                  </div>
                ))}
            </div>
            <div className="ops-note mt-3">{pricing.revenue?.note}</div>
          </div>
        )}
      </div>
      {/* Discount Modal */}
      {discountModalOpen && (
        <div
          className="ops-modal-overlay"
          onClick={() => setDiscountModalOpen(false)}
        >
          <div className="ops-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ops-modal-header">
              <span className="ops-modal-title">
                <i className="ti ti-tag" aria-hidden="true" />
                {pricing.discount_breakdown?.title || "Discount Breakdown"}
              </span>
              <button
                className="ops-modal-close"
                onClick={() => setDiscountModalOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="ops-modal-status mx-2">
              <p className="my-0 text-muted small">
                Total Discount :
                <span className=" fw-bold">
                  {pricing.discount_breakdown?.formatted_total_discount} EGP
                </span>
              </p>
              <span className={badgeConfig.className}>
                {badgeConfig.icon}
                {pricing.discount_breakdown?.status?.label}
              </span>
            </div>
            <div className=" mb-2">
              <span className="ops-modal-applied-on mx-2">
                Applied on: {pricing.discount_breakdown?.applied_on?.label}
              </span>
            </div>
            <div className="ops-modal-table-wrap">
              <table className="ops-modal-table">
                <thead>
                  <tr>
                    {pricing.discount_breakdown?.columns?.map((col) => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pricing.discount_breakdown?.services?.map((svc, idx) => (
                    <tr key={idx}>
                      <td>{svc.label}</td>
                      <td>{svc.formatted?.base_price} EGP</td>
                      <td className="ops-discount-total-val">
                        -{svc.formatted?.discount} EGP
                      </td>
                      <td>
                        {svc.sources?.map((s) => (
                          <span className="ops-chip ops-chip--sm" key={s.key}>
                            {s.label}
                          </span>
                        ))}
                      </td>
                      <td className="ops-discount-total-val">
                        -{svc.formatted?.discount} EGP
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ops-modal-footer">
              {pricing.discount_breakdown?.note && (
                <div className="ops-note">
                  {pricing.discount_breakdown.note}
                </div>
              )}
              <div className="ops-modal-covered">
                <span className="ops-label">
                  {pricing.discount_summary?.covered_by?.label}:
                </span>
                <div className="ops-covered-chips">
                  {pricing.discount_summary?.covered_by?.estbn > 0 && (
                    <span className="ops-chip">
                      ESTBN:{" "}
                      {pricing.discount_summary.covered_by.formatted.estbn} EGP
                    </span>
                  )}
                  {pricing.discount_summary?.covered_by?.provider > 0 && (
                    <span className="ops-chip">
                      Provider:{" "}
                      {pricing.discount_summary.covered_by.formatted.provider}{" "}
                      EGP
                    </span>
                  )}
                  {pricing.discount_summary?.covered_by?.unknown > 0 && (
                    <span className="ops-chip">
                      Unknown:{" "}
                      {pricing.discount_summary.covered_by.formatted.unknown}{" "}
                      EGP
                    </span>
                  )}
                </div>
              </div>
              <div className="ops-modal-total-row">
                <span className="ops-label">Total Discount:</span>
                <span className="ops-discount-total-val ops-value">
                  -{pricing.discount_breakdown?.formatted_total_discount} EGP
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderPricingSummary;
